'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { 
  LayoutDashboard, 
  Package, 
  ShoppingBag, 
  FolderTree, 
  CreditCard, 
  Home,
  Menu,
  X,
  ArrowLeft,
  Settings,
  Users
} from 'lucide-react';
import { useAuthStore } from '@/store/useAuthStore';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/ui';

interface DashboardShellProps {
  role: 'seller' | 'admin';
  title?: string;
  children: React.ReactNode;
}

const sellerNavItems = [
  { href: '/seller/products', label: 'Бараа', icon: Package, requiredRole: ['seller', 'admin'] as const },
  { href: '/seller/orders', label: 'Захиалга', icon: ShoppingBag, requiredRole: ['seller', 'admin'] as const },
  { href: '/seller/settings', label: 'Дэлгүүрийн тохиргоо', icon: Settings, requiredRole: ['seller', 'admin'] as const },
];

const adminNavItems = [
  { href: '/admin/dashboard', label: 'Самбар', icon: LayoutDashboard, requiredRole: ['admin'] as const },
  { href: '/admin/categories', label: 'Ангилал', icon: FolderTree, requiredRole: ['admin'] as const },
  { href: '/admin/products', label: 'Бараа', icon: Package, requiredRole: ['admin'] as const },
  { href: '/admin/orders', label: 'Захиалга', icon: ShoppingBag, requiredRole: ['admin'] as const },
  { href: '/admin/users', label: 'Хэрэглэгчид', icon: Users, requiredRole: ['admin'] as const },
  { href: '/admin/payments/qpay', label: 'QPay төлбөр', icon: CreditCard, requiredRole: ['admin'] as const },
  { href: '/admin/homepage', label: 'Нүүр хуудас', icon: Home, requiredRole: ['admin'] as const },
];

export function DashboardShell({ role, title, children }: DashboardShellProps) {
  const pathname = usePathname();
  const { user } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Get nav items based on role prop, but filter by actual user role
  const allNavItems = role === 'seller' ? sellerNavItems : adminNavItems;
  const navItems = allNavItems.filter(item => {
    if (!user) return false;
    return item.requiredRole.includes(user.role as any);
  });

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 lg:justify-center">
            <h2 className="text-lg font-bold text-gray-900">
              {role === 'seller' ? 'Худалдагч' : 'Админ'}
            </h2>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = pathname === item.href;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  )}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Back to shop link */}
          <div className="p-4 border-t border-gray-200">
            <Link
              href="/"
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Дэлгүүр рүү буцах
            </Link>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
              {title && <h1 className="text-2xl font-bold text-gray-900">{title}</h1>}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}

